import { Button, Modal } from "@design";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { GoogleDriveItem, SharePointItem } from "@/api";
import { SharePointPicker } from "@/features/build/dataSources/SharePointPicker";
import { loadDriveDoc, loadSharePointDoc } from "./cloudFiles";
import { GoogleDrivePicker } from "./GoogleDrivePicker";
import type { CloudFile } from "./useAttachments";

export type CloudSource = "sharepoint" | "googleDrive";

/** Pick SharePoint/OneDrive or Google Drive files to attach to the next message. */
export function CloudFilesDialog({
  source,
  onClose,
  onAttach,
}: Readonly<{ source: CloudSource | null; onClose: () => void; onAttach: (files: CloudFile[]) => void }>) {
  const { t } = useTranslation("chat", { keyPrefix: "cloud" });
  const [files, setFiles] = useState<CloudFile[]>([]);
  const close = () => {
    setFiles([]);
    onClose();
  };
  const pickSharePoint = (items: SharePointItem[]) =>
    setFiles(items.map((item) => ({ name: item.name, mimeType: item.mime_type, load: () => loadSharePointDoc(item) })));
  const pickDrive = (items: GoogleDriveItem[]) =>
    setFiles(items.map((item) => ({ name: item.name, mimeType: item.mime_type, load: () => loadDriveDoc(item) })));

  return (
    <Modal
      open={source !== null}
      onClose={close}
      title={source === "googleDrive" ? t("driveTitle") : t("sharepointTitle")}
      closeLabel={t("close")}
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={files.length === 0}
            onClick={() => {
              onAttach(files);
              close();
            }}
          >
            {t("attach", { count: files.length })}
          </Button>
        </>
      }
    >
      {source === "sharepoint" && <SharePointPicker onFiles={pickSharePoint} />}
      {source === "googleDrive" && <GoogleDrivePicker onFiles={pickDrive} />}
    </Modal>
  );
}
